import {
	BrowserOptions,
	Event,
	EventHint,
	init as SentryInit
} from "@sentry/react";
import { CaptureConsole, Dedupe } from "@sentry/integrations";
import shouldSentryLogMessage, { errors } from "@/V2Utils/commonErrors";

const REPORTING_APP_ENVIRONMENTS = ["production", "canary"];
const CONSOLE_EVENT_SAMPLE_RATE = 0.1;
const REPLAY_ON_ERROR_SAMPLE_RATE = 0.1;

export interface SentryEnvironment {
	nodeEnv?: string;
	appEnv?: string;
	dsn?: string;
	release?: string;
}

const safeStringify = (value: unknown): string | undefined => {
	try {
		return JSON.stringify(value);
	} catch {
		return undefined;
	}
};

export const beforeSendingEvent = (event: Event, hint?: EventHint) => {
	const updatedEvent = { ...event };

	const extraArguments = updatedEvent?.extra?.arguments as
		| Array<Record<string, string | number>>
		| undefined;

	const errorsWithCode = extraArguments?.filter(
		extra => extra?.code === 400 || extra?.code === 401
	);

	if (!!errorsWithCode?.length) return null;

	const shouldLogMessage = shouldSentryLogMessage(
		updatedEvent?.message ?? "",
		safeStringify(
			updatedEvent?.extra?.arguments ?? updatedEvent?.extra ?? ""
		)
	);

	if (!shouldLogMessage) return null;

	const isConsoleEvent = updatedEvent.logger === "console";

	if (isConsoleEvent && Math.random() >= CONSOLE_EVENT_SAMPLE_RATE) {
		return null;
	}

	if (
		isConsoleEvent &&
		updatedEvent.extra &&
		updatedEvent.message?.includes("[object Object]")
	) {
		const argsAsJSON = safeStringify(updatedEvent.extra.arguments)?.slice(
			1,
			-1
		);
		if (argsAsJSON !== undefined) updatedEvent.message = argsAsJSON;
	}

	updatedEvent.extra = {
		...updatedEvent.extra,
		hint:
			hint === undefined
				? undefined
				: safeStringify(hint) ?? String(hint.originalException ?? "")
	};

	return updatedEvent;
};

export const buildSentryOptions = ({
	dsn,
	appEnv,
	release
}: SentryEnvironment): BrowserOptions => ({
	enabled: true,
	dsn,
	ignoreErrors: errors,
	beforeSend: beforeSendingEvent,
	release,
	environment: appEnv,
	attachStacktrace: true,
	maxBreadcrumbs: 50,
	integrations: [new CaptureConsole({ levels: ["error"] }), new Dedupe()],
	replaysSessionSampleRate: 0,
	replaysOnErrorSampleRate: REPLAY_ON_ERROR_SAMPLE_RATE
});

export const shouldInitializeSentry = ({
	nodeEnv,
	appEnv,
	dsn
}: SentryEnvironment): boolean =>
	nodeEnv === "production" &&
	!!dsn &&
	!!appEnv &&
	REPORTING_APP_ENVIRONMENTS.includes(appEnv);

const currentSentryEnvironment = (): SentryEnvironment => ({
	nodeEnv: process.env.NODE_ENV,
	appEnv: process.env.REACT_APP_NODE_ENV,
	dsn: process.env.REACT_APP_SENTRY,
	release: process.env.REACT_APP_SHA
});

export const initializeSentry = (
	environment: SentryEnvironment = currentSentryEnvironment()
): void => {
	if (!shouldInitializeSentry(environment)) return;

	try {
		SentryInit(buildSentryOptions(environment));
	} catch (error) {
		console.error(error);
	}
};
